import { useQuery, useQueries } from '@tanstack/react-query';
import { executiveApi } from '../services/apiService';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import { TimePeriod } from '../types/api';
import { logger } from '../utils/logger';

// Query keys for cache management
export const EXECUTIVE_QUERY_KEYS = {
  DASHBOARD: 'executiveDashboard',
  KPIS: 'executiveKpis',
  FUNNEL: 'executiveFunnel',
  TRENDS: 'executiveTrends',
  PERFORMANCE: 'executivePerformance',
  CREDIT_USAGE_RATE: 'executiveCreditUsageRate',
  KEY_INSIGHTS: 'executiveKeyInsights',
  CREDIT_COMPARISON: 'executiveCreditComparison',
  TREND_ANALYSIS: 'executiveTrendAnalysis',
  PERFORMANCE_TABLE: 'executivePerformanceTable',
};

/**
 * Hook to fetch executive dashboard data with all parameters
 */
export function useExecutiveDashboard(
  branchId: number | undefined,
  options: {
    startMonth?: string;
    endMonth?: string;
    yearMonth?: string;
    timeRange?: TimePeriod;
  } = {}
) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use options.timeRange if provided, otherwise use the one from context
  const timeRange = options.timeRange || apiTimeFrame;
  
  // Create a new options object with the timeRange
  const mergedOptions = {
    ...options,
    timeRange
  };
  
  return useQuery({
    queryKey: [
      EXECUTIVE_QUERY_KEYS.DASHBOARD,
      branchId,
      options.startMonth,
      options.endMonth,
      options.yearMonth,
      timeRange,
    ],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await executiveApi.getDashboardData(branchId, mergedOptions);
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch executive dashboard data', 'ExecutiveQueries', error);
        // Return a minimal data structure to prevent UI errors
        return {
          dashboardData: [],
          kpis: {},
          funnelData: []
        };
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

/**
 * Hook to fetch executive KPIs
 */
export function useExecutiveKpis(branchId: number | undefined, timeRange?: TimePeriod, yearMonth?: string) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [EXECUTIVE_QUERY_KEYS.KPIS, branchId, yearMonth, effectiveTimeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await executiveApi.getKpis(branchId, { 
          yearMonth, 
          timeRange: effectiveTimeRange 
        });
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch executive KPIs', 'ExecutiveQueries', error);
        return {};
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch executive funnel data
 */
export function useExecutiveFunnel(branchId: number | undefined, timeRange?: TimePeriod, yearMonth?: string) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;

  return useQuery({
    queryKey: [EXECUTIVE_QUERY_KEYS.FUNNEL, branchId, yearMonth, effectiveTimeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await executiveApi.getFunnelData(branchId, {
          yearMonth,
          timeRange: effectiveTimeRange
        });
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch executive funnel data', 'ExecutiveQueries', error);
        return [];
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Comprehensive hook that fetches all executive dashboard data in parallel
 */
export function useCompleteExecutiveDashboard(
  branchId: number | undefined,
  brandId: number | undefined,
  options: {
    startMonth?: string;
    endMonth?: string;
    yearMonth?: string;
    timeRange?: TimePeriod;
  } = {}
) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use options.timeRange if provided, otherwise use the one from context
  const timeRange = options.timeRange || apiTimeFrame;
  
  // Create a new options object with the timeRange
  const mergedOptions = {
    ...options,
    timeRange
  };
  
  const results = useQueries({
    queries: [
      {
        queryKey: [EXECUTIVE_QUERY_KEYS.DASHBOARD, branchId, options.startMonth, options.endMonth, options.yearMonth, timeRange],
        queryFn: async () => {
          if (!branchId) throw new Error('Branch ID is required');
          
          try {
            const response = await executiveApi.getDashboardData(branchId, mergedOptions);
            return response.data;
          } catch (error) {
            logger.error('Failed to fetch executive dashboard data', 'ExecutiveQueries', error);
            return {
              dashboardData: [],
              kpis: {},
              funnelData: []
            };
          }
        },
        enabled: !!branchId,
      },
      {
        queryKey: [EXECUTIVE_QUERY_KEYS.KPIS, branchId, options.yearMonth, timeRange],
        queryFn: async () => {
          if (!branchId) throw new Error('Branch ID is required');
          
          try {
            const response = await executiveApi.getKpis(branchId, { 
              yearMonth: options.yearMonth, 
              timeRange 
            });
            return response.data;
          } catch (error) {
            logger.error('Failed to fetch executive KPIs', 'ExecutiveQueries', error);
            return {};
          }
        },
        enabled: !!branchId,
      },
      {
        queryKey: [EXECUTIVE_QUERY_KEYS.FUNNEL, branchId, options.yearMonth, timeRange],
        queryFn: async () => {
          if (!branchId) throw new Error('Branch ID is required');
          
          try {
            const response = await executiveApi.getFunnelData(branchId, {
              yearMonth: options.yearMonth,
              timeRange
            });
            return response.data;
          } catch (error) {
            logger.error('Failed to fetch executive funnel data', 'ExecutiveQueries', error);
            return [];
          }
        },
        enabled: !!branchId,
      }
    ]
  });

  // Extract individual query results
  const [dashboardResult, kpisResult, funnelResult] = results;
  
  // Combine the results
  const combined = {
    dashboardData: dashboardResult.data || {
      dashboardData: [],
      kpis: {},
      funnelData: []
    },
    kpis: kpisResult.data || {},
    funnel: funnelResult.data || [],
    loading: dashboardResult.isLoading || kpisResult.isLoading || funnelResult.isLoading,
    error: dashboardResult.error || kpisResult.error || funnelResult.error,
    refetch: () => {
      dashboardResult.refetch();
      kpisResult.refetch();
      funnelResult.refetch();
    }
  };
  
  return combined;
}

// New granular hooks for refactored components

/**
 * Hook to fetch credit usage rate data
 */
export function useExecutiveCreditUsageRate(branchId: number | undefined, timeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  
  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [EXECUTIVE_QUERY_KEYS.CREDIT_USAGE_RATE, branchId, effectiveTimeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await executiveApi.getCreditUsageRate(branchId, { timeRange: effectiveTimeRange });
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch credit usage rate', 'ExecutiveQueries', error);
        return {};
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch key insights data
 */
export function useExecutiveKeyInsights(branchId: number | undefined, timeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  
  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [EXECUTIVE_QUERY_KEYS.KEY_INSIGHTS, branchId, effectiveTimeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await executiveApi.getKeyInsights(branchId, { timeRange: effectiveTimeRange });
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch key insights', 'ExecutiveQueries', error);
        return {};
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch credit comparison data
 */
export function useExecutiveCreditComparison(branchId: number | undefined, timeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  
  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [EXECUTIVE_QUERY_KEYS.CREDIT_COMPARISON, branchId, effectiveTimeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await executiveApi.getCreditComparison(branchId, { timeRange: effectiveTimeRange });
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch credit comparison', 'ExecutiveQueries', error);
        return {};
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch trend analysis data
 */
export function useExecutiveTrendAnalysis(branchId: number | undefined, timeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  
  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [EXECUTIVE_QUERY_KEYS.TREND_ANALYSIS, branchId, effectiveTimeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await executiveApi.getTrendAnalysis(branchId, { timeRange: effectiveTimeRange });
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch trend analysis', 'ExecutiveQueries', error);
        return {};
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch performance table data
 */
export function useExecutivePerformanceTable(branchId: number | undefined, timeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  
  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;
  
  return useQuery({
    queryKey: [EXECUTIVE_QUERY_KEYS.PERFORMANCE_TABLE, branchId, effectiveTimeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      
      try {
        const response = await executiveApi.getPerformanceTable(branchId, { timeRange: effectiveTimeRange });
        return response.data;
      } catch (error) {
        logger.error('Failed to fetch performance table', 'ExecutiveQueries', error);
        return {};
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}